import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { CoreService } from '../../../../../../services/core/core.service';

interface ProductRoleOption {
  bundleId: 'viewer' | 'admin';
  bundleLabel: string;
  bundleDescription: string;
  roleKey: string;
}

/**
 * Alta/edición de usuarios "cliente" para `mb-web-client`, replicando la UX de `mb-web-admin`
 * pero acotada a la ÚNICA organización a la que pertenece el usuario en sesión.
 *
 * En vez de seleccionar organización, se usa la organización activa y se ofrece:
 *  - "¿Administrador de Organización?" (Sí/No).
 *  - Si No: por cada producto contratado por la organización, el rol del usuario
 *    ("Usuario General" = viewer / "Administrador de Producto" = admin).
 */
@Component({
  selector: 'app-configure-user-dialog',
  standalone: false,
  templateUrl: './configure-user-dialog.component.html',
  styleUrl: './configure-user-dialog.component.scss',
  providers: [ConfirmationService, MessageService],
})
export class ConfigureUserDialogComponent implements OnInit {
  isNew = false;
  isLoading = false;
  showPassword = false;

  private currentOrganizationId: string | null = null;
  organizationName = '';

  /** Productos contratados por la organización (servicios de la suscripción vigente). */
  contractedServices: string[] = [];
  /** ¿El usuario será administrador de la organización? */
  isOrgAdmin = false;
  /** Opciones de rol por producto (viewer/admin). */
  serviceBundleOptions: { [service: string]: ProductRoleOption[] } = {};
  /** Rol seleccionado por producto. */
  selectedServiceBundle: { [service: string]: 'viewer' | 'admin' } = {};

  formProfile: FormGroup;

  readonly orgAdminOptions = [
    { label: 'Sí', value: true },
    { label: 'No', value: false },
  ];

  private static readonly SERVICE_LABELS: Record<string, string> = {
    airnode: 'Airnode',
    lelfunko: 'Lelfunko',
    birdattack: 'BirdAttack',
  };

  /** Servicios internos que no se ofrecen como producto en el formulario. */
  private static readonly INTERNAL_SERVICES = new Set(['management', 'managment', 'general', 'platform']);

  constructor(
    private dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private coreService: CoreService
  ) {
    this.isNew = !!this.config.data?.isNew;
    const user = this.config.data?.user;

    this.formProfile = this.formBuilder.group({
      uid: [this.isNew ? '' : user?.user_id],
      firstName: [this.isNew ? '' : user?.first_name, Validators.compose([Validators.required, Validators.minLength(2)])],
      lastName: [this.isNew ? '' : user?.last_name, Validators.compose([Validators.required, Validators.minLength(2)])],
      email: [
        { value: this.isNew ? '' : user?.email, disabled: !this.isNew },
        Validators.compose([Validators.required, Validators.minLength(5), Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$')]),
      ],
      tempPasswordMode: [{ value: 'random', disabled: false }, Validators.required],
      password: [{ value: '', disabled: true }],
    });

    // Habilitar/validar contraseña solo cuando el modo es 'custom' (solo en creación).
    this.formProfile.get('tempPasswordMode')?.valueChanges.subscribe((mode: 'random' | 'custom') => {
      const pwd = this.formProfile.get('password');
      if (mode === 'custom' && this.isNew) {
        pwd?.enable();
        pwd?.setValidators([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(16),
          Validators.pattern('^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[\\W_]).+$'),
        ]);
      } else {
        pwd?.reset();
        pwd?.clearValidators();
        pwd?.disable();
      }
      pwd?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.currentOrganizationId = this.config?.data?.organizationId || this.coreService.resolveCurrentOrganizationId();
    if (!this.currentOrganizationId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Sin organización activa',
        detail: 'No se pudo determinar la organización activa. Vuelva a iniciar sesión o seleccione una organización.',
        life: 5000,
      });
      return;
    }

    this.organizationName = this.coreService.getCachedOrganizationName(this.currentOrganizationId) || '';

    // Productos contratados por la organización (desde la suscripción vigente).
    this.contractedServices = (this.coreService.getAvailableServices() || [])
      .map((s) => String(s || '').trim().toLowerCase())
      .filter((s) => s && !ConfigureUserDialogComponent.INTERNAL_SERVICES.has(s));

    for (const service of this.contractedServices) {
      this.serviceBundleOptions[service] = [
        { bundleId: 'viewer', bundleLabel: 'Usuario General', bundleDescription: 'Permisos de visualización del producto', roleKey: `${service}_viewer` },
        { bundleId: 'admin', bundleLabel: 'Administrador de Producto', bundleDescription: 'Permisos de visualización y edición del producto', roleKey: `${service}_admin` },
      ];
      this.selectedServiceBundle[service] = 'viewer';
    }

    // En edición: preseleccionar a partir de los roles actuales del usuario en la organización.
    if (!this.isNew) {
      const roles = this.extractCurrentRoles();
      this.applyRoleSelection(roles);
    }
  }

  /** Extrae los roles actuales del usuario para la organización (modo edición). */
  private extractCurrentRoles(): string[] {
    const membership = this.config?.data?.user?.currentMembership;
    const roles = membership?.roles;
    if (Array.isArray(roles)) {
      return roles.map((r: any) => String(r || '').trim()).filter(Boolean);
    }
    if (typeof roles === 'string' && roles.trim()) {
      return roles.split(',').map((r) => r.trim()).filter(Boolean);
    }
    return [];
  }

  private applyRoleSelection(roleKeys: string[]): void {
    this.isOrgAdmin = roleKeys.includes('management_org_admin') || roleKeys.includes('management_super_admin');

    for (const roleKey of roleKeys) {
      const service = roleKey.split('_')[0];
      if (ConfigureUserDialogComponent.INTERNAL_SERVICES.has(service)) {
        continue;
      }
      const option = this.serviceBundleOptions[service]?.find((o) => o.roleKey === roleKey);
      if (option) {
        this.selectedServiceBundle[service] = option.bundleId;
      }
    }

    if (this.isOrgAdmin) {
      this.applyOrgAdminMode();
    }
  }

  onOrgAdminModeChange(isOrgAdmin: boolean): void {
    this.isOrgAdmin = isOrgAdmin === true;
    if (this.isOrgAdmin) {
      this.applyOrgAdminMode();
    } else {
      this.applyGeneralUserMode();
    }
  }

  private applyOrgAdminMode(): void {
    for (const service of this.contractedServices) {
      this.selectedServiceBundle[service] = 'admin';
    }
  }

  private applyGeneralUserMode(): void {
    for (const service of this.contractedServices) {
      this.selectedServiceBundle[service] = this.selectedServiceBundle[service] || 'viewer';
    }
  }

  /** Roles finales a enviar: management_org_(admin|viewer) + roles por producto. */
  private resolveRoles(): string[] {
    const roles: string[] = [];
    roles.push(this.isOrgAdmin ? 'management_org_admin' : 'management_org_viewer');

    if (this.isOrgAdmin) {
      for (const service of this.contractedServices) {
        roles.push(`${service}_admin`);
      }
    } else {
      for (const service of this.contractedServices) {
        const bundleId = this.selectedServiceBundle[service];
        const option = this.serviceBundleOptions[service]?.find((o) => o.bundleId === bundleId);
        if (option?.roleKey) {
          roles.push(option.roleKey);
        }
      }
    }

    return Array.from(new Set(roles.filter(Boolean))).sort();
  }

  getServiceDisplayLabel(service: string): string {
    const normalized = (service || '').trim().toLowerCase();
    return ConfigureUserDialogComponent.SERVICE_LABELS[normalized] ?? (normalized.charAt(0).toUpperCase() + normalized.slice(1));
  }

  get isFormValid(): boolean {
    return this.formProfile.valid;
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  getErrorMessage(controlName: string): string {
    const control = this.formProfile.get(controlName);
    if (control?.hasError('required')) return 'Este campo es requerido.';
    if (control?.hasError('minlength')) return `Debe tener mínimo ${control.getError('minlength').requiredLength} caracteres.`;
    if (control?.hasError('maxlength')) return `Debe tener máximo ${control.getError('maxlength').requiredLength} caracteres.`;
    if (control?.hasError('pattern')) return 'El valor ingresado no cumple con el formato requerido.';
    return '';
  }

  hideDialog(): void {
    this.dialogRef.close();
  }

  saveConfiguration(): void {
    this.messageService.clear();

    if (this.formProfile.invalid) {
      this.formProfile.markAllAsTouched();
      this.messageService.add({ severity: 'error', summary: 'Campos inválidos', detail: 'Complete los campos requeridos correctamente.' });
      return;
    }

    const orgId = this.currentOrganizationId || this.coreService.resolveCurrentOrganizationId();
    if (!orgId) {
      this.messageService.add({ severity: 'error', summary: 'Sin organización activa', detail: 'No se encontró una organización activa en la sesión.' });
      return;
    }

    if (this.isNew) {
      this.confirmCreate(orgId);
    } else {
      this.confirmUpdate(orgId);
    }
  }

  private confirmCreate(orgId: string): void {
    const email = this.formProfile.controls['email'].value;
    this.confirmationService.confirm({
      header: '¿Está seguro de crear este nuevo usuario?',
      message: `Se enviará una invitación a ${email}. El usuario quedará invitado hasta que configure su contraseña en el primer acceso.`,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      acceptButtonStyleClass: 'p-button-success p-button-sm',
      accept: async () => {
        this.isLoading = true;
        const tempMode = this.formProfile.controls['tempPasswordMode'].value || 'random';
        const givenName = this.formProfile.controls['firstName'].value;
        const familyName = this.formProfile.controls['lastName'].value;
        const params = {
          email,
          given_name: givenName,
          family_name: familyName,
          organization_id: orgId,
          roles: this.resolveRoles(),
          scope: 'org' as const,
          temp_password_mode: tempMode,
          ...(tempMode === 'custom'
            ? {
                custom_temporary_password: this.formProfile.controls['password'].value,
                temporary_password: this.formProfile.controls['password'].value,
              }
            : {}),
          display_name: `${givenName} ${familyName}`.trim(),
        };

        try {
          await this.coreService.createAndAssignUser(params as any);
          this.dialogRef.close({
            status: 'success',
            created: true,
            severity: 'success',
            summary: 'Operación Exitosa',
            detail: `${givenName} ${familyName} ha sido creado correctamente`,
            life: 3000,
          });
        } catch (error) {
          this.handleError(error, 'Error al crear usuario');
        } finally {
          this.isLoading = false;
        }
      },
    });
  }

  private confirmUpdate(orgId: string): void {
    this.confirmationService.confirm({
      header: '¿Está seguro de actualizar la información del usuario?',
      message: 'Se actualizarán los datos y permisos del usuario en la organización.',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      acceptButtonStyleClass: 'p-button-success p-button-sm',
      accept: async () => {
        this.isLoading = true;
        const givenName = this.formProfile.controls['firstName'].value;
        const familyName = this.formProfile.controls['lastName'].value;
        const payload = {
          user_id: this.config.data.user.user_id,
          organization_id: orgId,
          roles: this.resolveRoles(),
          display_name: `${givenName} ${familyName}`.trim(),
          given_name: givenName,
          family_name: familyName,
        };

        try {
          const response = await this.coreService.updateAndAssignUser(payload as any);
          if (response?.message) {
            this.dialogRef.close({
              status: 'success',
              updated: true,
              severity: 'success',
              summary: 'Operación Exitosa',
              detail: response.message,
              life: 3000,
            });
          } else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Hubo un problema al actualizar al usuario.', life: 6000 });
          }
        } catch (error) {
          this.handleError(error, 'Error al actualizar');
        } finally {
          this.isLoading = false;
        }
      },
    });
  }

  private handleError(error: unknown, fallbackSummary: string): void {
    this.messageService.clear();
    if (error instanceof HttpErrorResponse) {
      this.messageService.add({
        severity: 'error',
        summary: `Error ${error.status}`,
        detail: error.error?.message || 'Ha ocurrido un error en la comunicación con el servidor.',
        life: 6000,
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: fallbackSummary,
        detail: (error as any)?.message || 'Ocurrió un error inesperado.',
        life: 6000,
      });
    }
  }
}
